import {
  ROUTES,
  buildPath,
  type ChatMessage,
  type ChatResponse,
  type ErrorLabel,
  type ErrorSheetResponse,
  type ReviewItem,
  type StepStatsRow,
  type Decision,
  type Experiment,
  type LearningOverview,
  type PlaybookVersion,
  type Report,
  type ErrorCode,
  type EventsResponse,
  type MeResponse,
  type MetricsResponse,
  type Org,
  type Reel,
  type ReelDetail,
  type RouteName,
  type ScriptOp,
  type ScriptResponse,
  type UploadResponse,
} from '@reelmachine/core';
import type { z } from 'zod';

/**
 * Assistant edits on fields a person changed while the assistant was thinking (review F08). They are not
 * applied; the person applies or dismisses them. Mirrors `rebaseOps` (`conflicting`, `conflictFields`).
 * Additive to `ChatResponse` until core declares it.
 */
export interface ChatProposal {
  /** Script version the assistant wrote the ops against. */
  baseVersion: number;
  ops: ScriptOp[];
  conflictFields: string[];
}
export type ChatResponseWithProposal = ChatResponse & { proposal?: ChatProposal };

/** Response type per route, as documented in the core contract. */
export interface RouteResponses {
  health: { ok: boolean };
  me: MeResponse;
  getOrg: { org: Org };
  updateOrg: { org: Org };
  readChannel: { org: Org };
  connectZernio: { url: string };
  zernioCallback: never;
  listReels: { reels: Reel[] };
  createReel: ReelDetail;
  upload: UploadResponse;
  getReel: ReelDetail;
  patchScript: ScriptResponse;
  regenerate: ReelDetail;
  addImages: ReelDetail;
  cancelJob: ReelDetail;
  listChat: { messages: ChatMessage[] };
  chat: ChatResponseWithProposal;
  undo: ScriptResponse;
  approve: ReelDetail;
  addTake: ReelDetail;
  selectTake: ReelDetail;
  render: ReelDetail;
  writePost: ReelDetail;
  setCaption: ReelDetail;
  publish: ReelDetail;
  overrideQuality: ReelDetail;
  recheckQuality: ReelDetail;
  resolvePublication: ReelDetail;
  events: EventsResponse;
  learningOverview: LearningOverview;
  listReports: { reports: Array<Omit<Report, 'body'>> };
  getReport: { report: Report };
  listDecisions: { decisions: Decision[] };
  decide: { decision: Decision };
  listExperiments: { experiments: Experiment[] };
  stopExperiment: { experiment: Experiment };
  listPlaybook: { versions: PlaybookVersion[] };
  rollbackPlaybook: { version: PlaybookVersion };
  errorSheet: ErrorSheetResponse;
  stepStats: { rows: StepStatsRow[] };
  reviewQueue: { items: ReviewItem[] };
  labelStep: { label: ErrorLabel };
}
export type { MetricsResponse };

type Routes = typeof ROUTES;
/** Request body type per route: the input type of the route's Zod schema, or `never` for routes without a body. */
export type BodyOf<R extends RouteName> = Routes[R] extends { body: infer S extends z.ZodTypeAny } ? z.input<S> : never;
type PathParamNames<P extends string> = P extends `${string}:${infer K}/${infer Rest}`
  ? K | PathParamNames<`/${Rest}`>
  : P extends `${string}:${infer K}`
    ? K
    : never;
export type ParamsOf<R extends RouteName> = Record<PathParamNames<Routes[R]['path']>, string>;

export type BodyArgs<R extends RouteName> = [BodyOf<R>] extends [never] ? [] : [body: BodyOf<R>];

const CODES: readonly ErrorCode[] = [
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'stage',
  'internal',
];

/** An error from the API, with the contract's error code. Network failures use code `network`. */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'network',
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function isApiError(e: unknown, code?: ApiError['code']): e is ApiError {
  return e instanceof ApiError && (code === undefined || e.code === code);
}

function codeForStatus(status: number): ErrorCode {
  if (status === 400 || status === 422) return 'bad_request';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  return 'internal';
}

/** Turns a non-2xx response into an ApiError, reading `{ error: { code, message } }` when present. */
export async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  const err = (body as { error?: { code?: unknown; message?: unknown; details?: unknown } } | undefined)?.error;
  const code = CODES.includes(err?.code as ErrorCode) ? (err!.code as ErrorCode) : codeForStatus(res.status);
  const message = typeof err?.message === 'string' && err.message ? err.message : `Request failed (${res.status})`;
  return new ApiError(code, message, res.status, err?.details);
}

export interface ApiClientOptions {
  /** Base URL of the API without `/v1`. Empty means the page origin. */
  baseUrl: string;
  getToken?: () => Promise<string | undefined> | string | undefined;
  onUnauthorized?: () => void;
  fetch?: typeof fetch;
}

/** Path parameters plus an optional query string (e.g. `{ query: { days: 28 } }`). */
export type CallParams<R extends RouteName> = ParamsOf<R> & { query?: Record<string, string | number> };

export interface ApiClient {
  call<R extends RouteName>(route: R, params: CallParams<R>, ...body: BodyArgs<R>): Promise<RouteResponses[R]>;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const base = () =>
    (opts.baseUrl || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/, '');

  async function call<R extends RouteName>(
    route: R,
    params: CallParams<R>,
    ...rest: BodyArgs<R>
  ): Promise<RouteResponses[R]> {
    const def = ROUTES[route];
    const body: unknown = rest[0];
    const { query, ...pathParams } = params as { query?: Record<string, string | number> } & Record<string, string>;
    const qs = query ? new URLSearchParams(Object.entries(query).map(([k, v]) => [k, String(v)])).toString() : '';
    const url = base() + buildPath(def.path, pathParams) + (qs ? `?${qs}` : '');
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = await opts.getToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
    let payload: string | undefined;
    if ('body' in def && body !== undefined) {
      payload = JSON.stringify(def.body.parse(body));
      headers['Content-Type'] = 'application/json';
    }
    let res: Response;
    try {
      res = await doFetch(url, { method: def.method, headers, body: payload });
    } catch (e) {
      throw new ApiError('network', 'Could not reach the server. Check your connection.', 0, e);
    }
    if (!res.ok) {
      const err = await toApiError(res);
      if (err.code === 'unauthorized') opts.onUnauthorized?.();
      throw err;
    }
    if (res.status === 204) return undefined as unknown as RouteResponses[R];
    return (await res.json()) as RouteResponses[R];
  }

  return { call };
}

/** Content types the upload route accepts. Other files are rejected in the picker. */
export const UPLOAD_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/quicktime',
] as const;

export function isAllowedUploadType(contentType: string): boolean {
  const base = contentType.split(';')[0]!.trim().toLowerCase();
  return (UPLOAD_TYPES as readonly string[]).includes(base) || /^audio\/[\w.+-]+$/.test(base);
}

/**
 * Uploads a file with the presigned POST from the upload route: every policy field first, then `file`
 * (S3 ignores fields after the file).
 */
export async function postToPresignedUrl(
  upload: UploadResponse,
  file: Blob,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const form = new FormData();
  for (const [k, v] of Object.entries(upload.fields)) form.append(k, v);
  form.append('file', file);
  let res: Response;
  try {
    res = await fetchImpl(upload.url, { method: upload.method ?? 'POST', body: form });
  } catch (e) {
    throw new ApiError('network', 'Upload failed. Check your connection.', 0, e);
  }
  if (!res.ok) {
    const message =
      res.status === 403 || res.status === 400
        ? 'The file was not accepted. Check its type and size.'
        : `Upload failed (${res.status})`;
    throw new ApiError(res.status === 403 || res.status === 400 ? 'bad_request' : 'internal', message, res.status);
  }
}
